import type { AdminLandingPageLink } from '@storefronts/common-types'
import { useQuery } from '@tanstack/react-query'
import React, {
    createContext,
    useCallback,
    useContext,
    useMemo,
    useRef,
    useState,
} from 'react'
import { landingPageLinksQueryKey } from '@/hooks/landingPageApiHooks'
import { R } from '../../modules/api/actions'
import { AuthContext } from '../../modules/auth/authContext'

export type LandingPageLinksContextValue = {
    allLandingPageLinks: AdminLandingPageLink[]
    selectableLandingPageLinks: AdminLandingPageLink[]
    isLoading: boolean
    refreshLandingPageLinks: () => Promise<void>
    loadFullLandingPageLinks: (
        linkHashes: string[],
    ) => Promise<AdminLandingPageLink[]>
}

const EMPTY_LINKS: AdminLandingPageLink[] = []

const defaultValue: LandingPageLinksContextValue = {
    allLandingPageLinks: EMPTY_LINKS,
    selectableLandingPageLinks: EMPTY_LINKS,
    isLoading: false,
    refreshLandingPageLinks: async () => {},
    loadFullLandingPageLinks: async () => EMPTY_LINKS,
}

export const LandingPageLinksContext =
    createContext<LandingPageLinksContextValue>(defaultValue)

export function useLandingPageLinks(): LandingPageLinksContextValue {
    const context = useContext(LandingPageLinksContext)
    return context ?? defaultValue
}

export const LandingPageLinksProvider: React.FC<{
    children: React.ReactNode
}> = ({ children }) => {
    const { client } = useContext(AuthContext)
    const clientHash = client?.clientHash ?? null

    const { data, isLoading, refetch } = useQuery({
        queryKey: landingPageLinksQueryKey(clientHash),
        queryFn: async () => {
            if (clientHash) {
                R.setClient(clientHash)
            }
            const res = await R.getAllLandingPageLinks()
            return res.landingPageLinks
        },
        enabled: Boolean(clientHash),
        staleTime: 5 * 60 * 1000,
    })

    // The catalog is slim (price 0, no variants). Products attached to the
    // page are swapped for their full links, kept apart so a refetch of the
    // catalog doesn't undo the swap.
    const [fullLinksByHash, setFullLinksByHash] = useState<
        ReadonlyMap<string, AdminLandingPageLink>
    >(new Map())
    const inFlightRef = useRef(
        new Map<string, Promise<AdminLandingPageLink | undefined>>(),
    )

    const allLandingPageLinks = useMemo(
        () =>
            fullLinksByHash.size === 0
                ? (data ?? EMPTY_LINKS)
                : (data ?? EMPTY_LINKS).map(
                      (link) => fullLinksByHash.get(link.linkHash) ?? link,
                  ),
        [data, fullLinksByHash],
    )

    const loadLinks = useCallback(async () => {
        await refetch()
    }, [refetch])

    const loadFullLandingPageLinks = useCallback(
        async (linkHashes: string[]) => {
            const wanted = new Set(linkHashes)
            const slimLinks = allLandingPageLinks.filter(
                (link) => wanted.has(link.linkHash) && !link.variantInfo,
            )
            if (slimLinks.length === 0) return allLandingPageLinks

            // Concurrent callers (backfill + picker) share one batch request.
            const inFlight = inFlightRef.current
            const toFetch = slimLinks.filter(
                (link) => !inFlight.has(link.linkHash),
            )
            if (toFetch.length > 0) {
                const batch = R.getLandingPageLinksBatch(
                    toFetch.map((link) => link.linkHash),
                )
                    .then((response) => response.landingPageLinks)
                    .catch(() => [])
                toFetch.forEach((slim) => {
                    const promise = batch.then((links) => {
                        const full = links.find(
                            (link) => link.linkHash === slim.linkHash,
                        )
                        if (!full) inFlight.delete(slim.linkHash)
                        // The batch is the shopper shape; keep the catalog's
                        // admin-only fields.
                        return full ? { ...slim, ...full } : undefined
                    })
                    inFlight.set(slim.linkHash, promise)
                })
            }
            const results = await Promise.all(
                slimLinks.map((link) => inFlight.get(link.linkHash)),
            )
            const loaded = results.filter(
                (link): link is AdminLandingPageLink => link != null,
            )
            if (loaded.length === 0) return allLandingPageLinks

            const loadedByHash = new Map(
                loaded.map((link) => [link.linkHash, link]),
            )
            setFullLinksByHash((previous) => {
                const next = new Map(previous)
                loaded.forEach((link) => next.set(link.linkHash, link))
                return next
            })
            return allLandingPageLinks.map(
                (link) => loadedByHash.get(link.linkHash) ?? link,
            )
        },
        [allLandingPageLinks],
    )

    const selectableLandingPageLinks = useMemo(
        () => allLandingPageLinks.filter((link) => !link.hasVirtualReplacement),
        [allLandingPageLinks],
    )

    const value = useMemo<LandingPageLinksContextValue>(
        () => ({
            allLandingPageLinks,
            selectableLandingPageLinks,
            isLoading,
            refreshLandingPageLinks: loadLinks,
            loadFullLandingPageLinks,
        }),
        [
            allLandingPageLinks,
            selectableLandingPageLinks,
            isLoading,
            loadLinks,
            loadFullLandingPageLinks,
        ],
    )

    return (
        <LandingPageLinksContext.Provider value={value}>
            {children}
        </LandingPageLinksContext.Provider>
    )
}
